import { useNavigate } from 'react-router-dom';

import { APRECIACAO_ROOT, setFlowChoice } from './eligibility';
import { FlowEligibility, TabKey } from './types';

const TAB_LABEL: Record<TabKey, string> = {
  diretoria: 'Diretoria',
  mad: 'MAD',
  rede: 'Rede',
};

interface FlowTabsProps {
  // Aba corrente. 'mad' identifica a página do fluxo MAD; 'diretoria'/'rede', a do Normal.
  active: TabKey;
  // Elegibilidade do gestor exibido — decide quais abas do OUTRO fluxo existem.
  eligibility: FlowEligibility | null;
  // Universo Rede disponível na fase (diretor cod_etapa 5 no semestre).
  hasRede?: boolean;
  // Fase em exibição — viaja junto na troca de fluxo para o gestor não ser jogado de
  // volta na fase corrente do calendário.
  currentStage?: string;
  // Troca de aba DENTRO do fluxo Normal (diretoria⇄rede); ausente na página MAD.
  onSelectTab?: (tab: 'diretoria' | 'rede') => void;
}

// Badge de pendência por fluxo (ELE-05): mostra a contagem quando o backend a projeta;
// destaca (flame) quando há pendência e apaga (muted) quando é zero, para o gestor
// elegível aos dois fluxos ver de imediato onde há trabalho. Sem contagem, não renderiza.
function PendingBadge({ count }: { count?: number }) {
  if (typeof count !== 'number') return null;

  return (
    <span
      className={`ml-2 h-5 min-w-5 px-1 text-xs rounded-full flex items-center justify-center ${
        count > 0 ? 'bg-flame text-white' : 'bg-oxford-blue text-alice-blue'
      }`}
      aria-label={`${count} pendente${count === 1 ? '' : 's'}`}
    >
      {count}
    </span>
  );
}

// Barra de abas Diretoria | MAD | Rede da apreciação — o mesmo segmentado do monólito
// (index.tsx:4490-4523), dentro do card "Realizações da Fase".
//
// Diretoria e Rede são universos do fluxo Normal: na própria página do Normal a troca é
// local (onSelectTab); vinda da página MAD, é navegação. MAD é sempre navegação a partir
// do Normal. Toda aba que muda de fluxo grava a escolha (apreciacao_flow_choice) antes de
// navegar, para o guarda-chuva devolver o gestor ao mesmo fluxo na sessão (ELE-02), e
// leva a fase em exibição no state da navegação, para a troca não mudar a fase.
export function FlowTabs({ active, eligibility, hasRede, currentStage, onSelectTab }: FlowTabsProps) {
  const navigate = useNavigate();

  const isMadPage = active === 'mad';

  // A aba da própria página sempre existe; as do outro fluxo dependem da elegibilidade —
  // não adianta oferecer um fluxo ao qual o gestor não tem acesso.
  const canOpenNormal = !isMadPage || !!eligibility?.normal;

  const tabs: TabKey[] = [
    ...(canOpenNormal ? (['diretoria'] as TabKey[]) : []),
    ...(isMadPage || eligibility?.mad ? (['mad'] as TabKey[]) : []),
    // A aba corrente sempre existe: 'rede' pode estar ativa antes de a fase resolver o
    // universo Rede (ex.: chegada da página MAD já pedindo essa aba).
    ...(canOpenNormal && (hasRede || active === 'rede') ? (['rede'] as TabKey[]) : []),
  ];

  // Uma aba só não é escolha — o monólito também não desenhava a linha nesse caso.
  if (tabs.length < 2) return null;

  // O badge marca a aba que leva ao OUTRO fluxo — é onde a contagem informa algo que a
  // própria lista não mostra. Nas abas do fluxo corrente ele só repetiria o mesmo número.
  const badgeTab: TabKey = isMadPage ? 'diretoria' : 'mad';
  const badgeCount = isMadPage ? eligibility?.normalPending : eligibility?.madPending;

  function handleSelect(tab: TabKey) {
    if (tab === active) return;

    if (tab === 'mad') {
      setFlowChoice('mad');
      navigate(`${APRECIACAO_ROOT}/mad`, { state: { stage: currentStage } });
      return;
    }

    // Vindo do MAD, diretoria/rede é troca de fluxo — abre o Normal já na aba pedida.
    if (isMadPage) {
      setFlowChoice('diretoria-rede');
      navigate(`${APRECIACAO_ROOT}/diretoria-rede`, { state: { tab, stage: currentStage } });
      return;
    }

    onSelectTab?.(tab);
  }

  return (
    <div className="flex items-center justify-between">
      {tabs.map(tab => (
        <button
          key={tab}
          className={`${
            tab === active ? 'text-alice-blue bg-slate-blue' : 'text-white'
          } text-sm flex items-center justify-center font-semibold uppercase ring-2 ring-slate-blue ${
            tabs.length === 3 ? 'rounded-none' : 'rounded'
          } w-[45%] h-10`}
          aria-pressed={tab === active}
          onClick={() => handleSelect(tab)}
        >
          {TAB_LABEL[tab]}
          {tab === badgeTab && <PendingBadge count={badgeCount} />}
        </button>
      ))}
    </div>
  );
}
